// features/products/packageOptions.ts
//
// Gom danh sách sản phẩm phẳng thành "gói" cho bộ chọn 2 bước của form đơn:
// nhóm sản phẩm → gói con → tick makeup. Các cặp "X (có make)" / "X (không
// make)" gộp thành MỘT gói với 2 biến thể; tick makeup quyết định biến thể
// nào được ghi xuống đơn (id sản phẩm giữ nguyên như cũ nên đơn cũ/báo cáo
// không đổi định dạng).
//
// Logic thuần tách khỏi ProductPicker vì Radix Select không mở được trong
// jsdom — test giao diện không với tới, nên phần quyết định tiền bạc phải
// test được ở đây.

import { splitMakeupSuffix } from "./packageLabel";
import type { ShootPurpose } from "./constants";

/**
 * Hạng gói ba bậc (QĐ 0130, CEO chốt 13/09/2026): chỉ thương hiệu mang cờ
 * `hasTiers` (Chụp Ảnh) mới có bước Hạng; gói chưa xếp hạng mang chuỗi rỗng.
 * Nhãn ghi MỘT nơi ở đây — bộ chọn gói, ô Hạng ở màn Sản phẩm và thẻ gói
 * cùng đọc, không chép tay mỗi nơi một kiểu.
 */
export type ProductTier = "co_ban" | "trung_cap" | "cao_cap";

/** Thứ tự nút hạng trên bộ chọn: rẻ trước, cao cấp sau. */
export const TIER_ORDER: readonly ProductTier[] = ["co_ban", "trung_cap", "cao_cap"];

export const TIER_LABEL: Record<ProductTier, string> = {
  co_ban: "Cơ bản",
  trung_cap: "Trung cấp",
  cao_cap: "Cao cấp",
};

/** Nhãn hạng; chuỗi rỗng / mã lạ → "Chưa xếp hạng" (không trả rỗng). */
export function tierLabel(tier: string | null | undefined): string {
  return tier && tier in TIER_LABEL ? TIER_LABEL[tier as ProductTier] : "Chưa xếp hạng";
}

/** Trường tối thiểu bộ chọn cần — dùng chung cho catalog tĩnh lẫn tuỳ chỉnh. */
export type PickerProduct = {
  id: string;
  name: string;
  price: number;
  /** Ảnh RIÊNG của gói (products.image_url) — ảnh bìa trong lưới chọn gói;
   *  không có = ô nền màu + biểu tượng. Ở THẺ XEM TRƯỚC thì đây chỉ là lưới
   *  đỡ: ảnh concept của gói được ưu tiên — xem packageImageOf. */
  imageUrl?: string;
  /** Hạng của gói (chỉ thương hiệu có hạng). Không khai = chưa xếp hạng. */
  tier?: ProductTier;
  /** Ảnh bìa là ảnh máy vẽ minh hoạ — ô lưới dán nhãn "ảnh minh hoạ". */
  imageIllustrative?: boolean;
  /** Mục đích chụp kèm giá riêng — có danh sách này thì bắt buộc chọn 1. */
  purposes?: ShootPurpose[];
  /** Concept gói cho phép chọn. Không khai báo = không giới hạn. */
  concepts?: string[];
  /** Thời lượng riêng của gói (phút) — gói nào khai thì đè mặc định của nhóm
   *  (vd CV 60' trong nhóm Ảnh thẻ 30'). Xem shootDurationMin. */
  durationMin?: number;
  /** Số người của BUỔI mà gói bao gồm. 0/không khai = gói chưa nói, form đơn
   *  cho lễ tân gõ tay. Xem OrderHeadcountField. */
  people?: number;
  /** Số người ĐƯỢC TRANG ĐIỂM gói bao gồm (products.makeup_people, 0253).
   *  Không khai = form đơn cho gõ tay. KHÁC `people`. */
  makeupPeople?: number;
  /** Số ảnh sửa kỹ gói cam kết — hiện ở thẻ xem trước gói của form đơn. */
  editPhotos?: number;
};

export interface SelectedProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  /** Danh sách mục đích của gói vừa chọn — form cha dựa vào đây để biết có
   *  bắt buộc chọn mục đích hay không. */
  purposes?: ShootPurpose[];
  /** Thời lượng riêng của gói (phút), nếu gói có khai. Form cha ghép với nhóm
   *  qua shootDurationMin để đặt lại thời lượng buổi chụp (SCH-01). */
  durationMin?: number;
  /** Số người của buổi mà gói khai. Không có = gói chưa khai, form cha mở ô
   *  cho lễ tân gõ tay. */
  people?: number;
  /** Số người được trang điểm gói khai. Không có = chưa khai. KHÁC `people`. */
  makeupPeople?: number;
}

/**
 * Gói vừa chọn, đúng hình dạng form cha nhận. Dùng chung cho cú bấm trên lưới
 * và cho form tự chọn sẵn gói (tạo đơn từ lượt khách tự đặt) — hai đường đi
 * qua cùng một hàm thì không lệch nhau về số người, thời lượng, mục đích.
 */
export function toSelectedProduct(catId: string, p: PickerProduct): SelectedProduct {
  return {
    id: p.id,
    name: p.name,
    category: catId,
    price: p.price,
    ...(p.purposes ? { purposes: p.purposes } : {}),
    ...(p.durationMin ? { durationMin: p.durationMin } : {}),
    ...(p.people ? { people: p.people } : {}),
    // `!= null` chứ không phải phép kiểm chân trị: 0 là lời khai THẬT ("gói
    // không có ai trang điểm"), rơi mất nó là ô lại mở ra bắt gõ tay.
    ...(p.makeupPeople != null ? { makeupPeople: p.makeupPeople } : {}),
  };
}

/** Danh sách mục đích của một sản phẩm (rỗng = gói không có mục đích). */
export function purposesOf(p: PickerProduct | null | undefined): ShootPurpose[] {
  return p?.purposes ?? [];
}

/**
 * Tra mục đích theo NHÃN đã lưu trên đơn (idphotoPurpose lưu nhãn hiển thị,
 * không lưu id — trang chi tiết in thẳng chuỗi này). Dùng khi đổi gói / tick
 * makeup: cùng nhãn tồn tại ở biến thể mới → giữ lựa chọn, lấy GIÁ MỚI.
 */
export function purposeByLabel(
  p: PickerProduct | null | undefined,
  label: string,
): ShootPurpose | null {
  if (!label) return null;
  return purposesOf(p).find((x) => x.label === label) ?? null;
}

export type PackageOption = {
  /** Giá trị ổn định cho <Select> gói. */
  key: string;
  /** Nhãn hiển thị: tên gói đã bỏ hậu tố make nếu là cặp đủ 2 vế. */
  label: string;
  /** Biến thể có make / không make (chỉ cặp mới có). */
  makeup?: PickerProduct;
  noMakeup?: PickerProduct;
  /** Gói không tách đôi (Profile, Doanh nghiệp, gói tuỳ chỉnh lẻ). */
  single?: PickerProduct;
};

/**
 * Ghép danh sách phẳng thành options theo thứ tự xuất hiện.
 * Cặp thiếu một vế (catalog tuỳ chỉnh chỉ thêm bản "(có make)") trả về dạng
 * gói đơn GIỮ NGUYÊN tên đầy đủ — không có gì để tick thì đừng cắt thông tin.
 */
export function buildPackageOptions(products: PickerProduct[]): PackageOption[] {
  const out: PackageOption[] = [];
  const pairIndex = new Map<string, PackageOption>();
  for (const p of products) {
    const { name: base, withMakeup } = splitMakeupSuffix(p.name);
    if (withMakeup === null) {
      out.push({ key: `id:${p.id}`, label: p.name, single: p });
      continue;
    }
    const norm = base.toLowerCase();
    let opt = pairIndex.get(norm);
    if (!opt) {
      opt = { key: `pair:${norm}`, label: base };
      pairIndex.set(norm, opt);
      out.push(opt);
    }
    if (withMakeup) opt.makeup = p;
    else opt.noMakeup = p;
  }
  return out.map((o) => {
    if (o.single || (o.makeup && o.noMakeup)) return o;
    const only = o.makeup ?? o.noMakeup;
    return only ? { key: `id:${only.id}`, label: only.name, single: only } : o;
  });
}

/** Gói này có cho tick chọn makeup không (đủ cả 2 biến thể)? */
export function hasMakeupChoice(opt: PackageOption): boolean {
  return Boolean(opt.makeup && opt.noMakeup);
}

/**
 * Sản phẩm thật sự được ghi xuống đơn khi chọn gói + trạng thái tick.
 * Gói đơn bỏ qua tick; cặp thiếu vế rơi về vế còn lại (không trả null oan).
 */
export function variantOf(
  opt: PackageOption,
  wantMakeup: boolean,
): PickerProduct | null {
  if (opt.single) return opt.single;
  return (wantMakeup ? opt.makeup : opt.noMakeup) ?? opt.makeup ?? opt.noMakeup ?? null;
}

export type PackageSelection = {
  option: PackageOption;
  /** true/false theo biến thể đang lưu; null = gói đơn, không có tick. */
  withMakeup: boolean | null;
};

/** Tra ngược id sản phẩm trên đơn → gói + trạng thái tick để hiển thị lại. */
export function selectionFromProductId(
  options: PackageOption[],
  productId: string,
): PackageSelection | null {
  if (!productId) return null;
  for (const option of options) {
    if (option.single?.id === productId) return { option, withMakeup: null };
    if (option.makeup?.id === productId) return { option, withMakeup: true };
    if (option.noMakeup?.id === productId) return { option, withMakeup: false };
  }
  return null;
}
